import { Pto } from '@outcoreaix/pto'
import { getQueryParamString } from 'utils/get-query-param-string'
import { apiSlice } from './api-splice'

export const usersApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    me: builder.query<Pto.Auth.JwtUser, void>({
      query: () => ({
        url: 'users/me',
        method: 'GET'
      })
    }),
    updateUser: builder.mutation<Pto.Auth.JwtUser, Pto.Users.UpdateUser>({
      query: (data) => ({
        url: 'users',
        method: 'PATCH',
        body: data
      }),
      invalidatesTags: ['Users']
    }),
    sendVerificationCode: builder.mutation<void, Pto.Users.SendVerificationCode>({
      query: (body) => ({
        url: 'users/send-verification-code',
        method: 'POST',
        body
      })
    }),
    phoneVerification: builder.mutation<Pto.Auth.JwtUser, Pto.Users.PhoneVerification>({
      query: (body) => ({
        url: 'users/phone-verification',
        method: 'POST',
        body
      })
    }),
    resendInvitation: builder.mutation<void, Pto.Users.ResendInvite>({
      query: (body) => ({
        url: 'users/resend-invitation',
        method: 'POST',
        body
      }),
      invalidatesTags: ['SpaceMembers', { type: 'Workspaces' }, 'Members']
    }),
    resendInvitationByToken: builder.mutation<void, { oldToken: string }>({
      query: ({ oldToken }) => ({
        url: `users/resend-invitation-by-token/${oldToken}`,
        method: 'POST'
      })
    }),
    resendOneTimeLink: builder.mutation<void, Pto.Users.ResendOneTimeLink>({
      query: (body) => ({
        url: 'users/resend-one-time-link',
        method: 'POST',
        body
      })
    }),
    getOneTimeToken: builder.query<Pto.Users.OneTimeToken, string>({
      query: (token) => ({
        url: `users/one-time-token/${token}`,
        method: 'GET'
      })
    }),
    incidentReport: builder.mutation<void, Pto.Users.IncidentReport>({
      query: (body) => ({
        url: 'users/incident-report',
        method: 'POST',
        body
      })
    }),
    countInvitations: builder.query<{ total: number }, void>({
      query: () => ({
        url: 'users/count-invitations',
        method: 'GET'
      })
    }),
    resendAllInvites: builder.mutation<void, void>({
      query: () => ({
        url: 'users/resend-all-invites',
        method: 'POST'
      })
    }),
    submitFeedback: builder.mutation<void, Pto.Users.SendFeedback>({
      query: (body) => ({
        url: 'users/feedbacks',
        method: 'POST',
        body
      }),
      invalidatesTags: ['Feedback']
    }),
    getFeedback: builder.query<Pto.Users.Feedback, void>({
      query: () => ({
        url: 'users/feedback',
        method: 'GET'
      }),
      providesTags: ['Feedback']
    }),
    getFeedbacks: builder.query<Pto.Users.FeedbackList, Pto.Users.FeedbackListQuery>({
      query: (params) => {
        const queryParams = getQueryParamString(params)
        return {
          url: `users/feedbacks?${queryParams}`,
          method: 'GET'
        }
      }
    }),
    getFeedbacksHistory: builder.query<Pto.Users.FeedbackHistoryList, Pto.Users.FeedbackHistoryListQuery>({
      query: (params) => {
        const queryParams = getQueryParamString(params)
        return {
          url: `users/feedbacks-history?${queryParams}`,
          method: 'GET'
        }
      }
    }),
    deleteFeedback: builder.mutation<Pto.Users.Feedback, void>({
      query: () => ({
        url: 'users/feedbacks',
        method: 'DELETE'
      }),
      invalidatesTags: ['Feedback']
    }),
    sendCareerNotifications: builder.mutation<
      Pto.Users.CareerNotificationUserResponse,
      Pto.Users.CareerNotificationUserQuery
    >({
      query: (users) => ({
        url: 'users/send-career-notifications',
        method: 'POST',
        body: users
      })
    })
  })
})

export const {
  useMeQuery,
  useIncidentReportMutation,
  useCountInvitationsQuery,
  useResendAllInvitesMutation,
  useResendInvitationMutation,
  useResendInvitationByTokenMutation,
  useResendOneTimeLinkMutation,
  useGetOneTimeTokenQuery,
  useUpdateUserMutation,
  useSendVerificationCodeMutation,
  usePhoneVerificationMutation,
  useSubmitFeedbackMutation,
  useGetFeedbackQuery,
  useGetFeedbacksQuery,
  useGetFeedbacksHistoryQuery,
  useDeleteFeedbackMutation,
  useSendCareerNotificationsMutation
} = usersApi
